import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useCountries } from '@bluheadless/ui-logic/src/hooks/useCountries'
import { useFormContext } from 'react-hook-form'
import { useFormFields } from '@bluheadless/ui-logic/src/hooks/form/useFormFields'
import { bool, object } from 'prop-types'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import Typography from '../../atoms/typography'
import { ControlledCheckbox, ControlledCheckboxGroup } from '../../molecules/checkbox'
import { ControlledDateTimePicker } from '../../molecules/date-picker'
import { ControlledDropdown } from '../../molecules/dropdown'
import { ControlledInput, ControlledInputTelephone } from '../../molecules/input'
import RichContent from '../../particles/rich-content'
import { ActionWrapper, FormWrapper, PrivacyWrapper, SubmitButtonStyled } from './newsletter-form.styled'

const NewsletterFormFull = ({ success, email, buttonProps, checkboxProps }) => {
	const {
		disclaimers: { marketing },
		newsletter: {
			popup: { fields },
		},
	} = useConfig()
	const { formatMessage } = useIntl()
	const { allCountries } = useCountries()

	const {
		setValue,
		reset,
		formState: { isSubmitSuccessful },
	} = useFormContext()

	const {
		collections: collectionsField,
		country: countryField,
		dob: dobField,
		email: emailField,
		firstName: firstNameField,
		gender: genderField,
		lastName: lastNameField,
		telephone: telephoneField,
		privacyPolicy: privacyPolicyField,
	} = useFormFields()

	useEffect(() => {
		if (isSubmitSuccessful && success) {
			const formDefaultValues = {
				[collectionsField.name]: {},
				[countryField.name]: '',
				[dobField.name]: '',
				[emailField.name]: '',
				[genderField.name]: '',
				[firstNameField.name]: '',
				[lastNameField.name]: '',
				[telephoneField.name]: '',
			}
			reset(formDefaultValues)
		}
	}, [
		collectionsField.name,
		countryField.name,
		dobField.name,
		emailField.name,
		genderField.name,
		firstNameField.name,
		lastNameField.name,
		telephoneField.name,
		isSubmitSuccessful,
		reset,
		success,
	])

	useEffect(() => {
		if (email) {
			setValue('email', email)
		}
	}, [email, setValue])

	return (
		<FormWrapper>
			{fields.firstname.enabled && (
				<ControlledInput required={fields.firstname.required} name={firstNameField.name} label={firstNameField.label} />
			)}
			{fields.lastname.enabled && (
				<ControlledInput required={fields.lastname.required} name={lastNameField.name} label={lastNameField.label} />
			)}
			{fields.gender.enabled && (
				<ControlledDropdown
					name={genderField.name}
					required={fields.gender.required}
					label={genderField.label}
					options={fields.gender.values.map(({ code, label }) => ({
						label: label,
						value: code,
					}))}
				/>
			)}
			{fields.dob.enabled && (
				<ControlledDateTimePicker
					name={dobField.name}
					required={fields.dob.required}
					label={dobField.label}
					maxDate={new Date()}
				/>
			)}
			{fields.country.enabled && (
				<ControlledDropdown
					name={countryField.name}
					required={fields.country.required}
					label={countryField.label}
					options={allCountries.map(({ label, code }) => ({
						label,
						value: code,
					}))}
				/>
			)}
			<ControlledInput required={true} name={emailField.name} label={emailField.label} />
			{fields.telephone.enabled && (
				<ControlledInputTelephone
					required={fields.telephone.required}
					name={telephoneField.name}
					label={telephoneField.label}
				/>
			)}

			{fields.collections.enabled && fields.collections.values.length > 0 && (
				<ControlledCheckboxGroup
					required={fields.collections.required}
					row={true}
					name={collectionsField.name}
					label={collectionsField.label}
					items={fields.collections.values.map(({ code, label }) => ({
						id: code,
						name: code,
						label: label,
					}))}
				/>
			)}

			<PrivacyWrapper>
				<ControlledCheckbox
					{...checkboxProps}
					name={privacyPolicyField.name}
					required
					label={
						<Typography variant="checkbox" color="grey.main" component="div">
							<RichContent content={marketing} />
						</Typography>
					}
				/>
			</PrivacyWrapper>

			<ActionWrapper>
				<SubmitButtonStyled {...buttonProps} label={formatMessage({ id: 'forms_newsletter_subscribe_btn' })} />
			</ActionWrapper>
		</FormWrapper>
	)
}

NewsletterFormFull.propTypes = {
	/**
	 *  Success is true if response status is 200
	 */
	success: bool.isRequired,
	/**
	 * Defines the properties of the subscribe button
	 */
	buttonProps: object,
	/**
	 * Defines the properties of the privacy checkbox
	 */
	checkboxProps: object,
}

export default NewsletterFormFull
